import {
  type QueryFunctionContext,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { HTTPError } from '~/services/kyClient';

import { queryKeysCompany } from '../queryKeys';

import { fetchCompanyAiSettings } from './fetchCompanyAiSettings';
import { type CompanyAiSettings } from './types';

/**
 * Generates the query options for the company AI settings query.
 *
 * @param companyId - The ID of the company to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getCompanyAiSettingsQueryOptions = <TData>({
  companyId,
  options,
}: {
  companyId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<CompanyAiSettings, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) => ({
  enabled: Boolean(companyId),
  queryFn: async ({ signal }: QueryFunctionContext) =>
    fetchCompanyAiSettings(companyId!, { signal }),
  queryKey: queryKeysCompany.getAiSettings(companyId ?? ''),
  retry(_failureCount: number, error: unknown) {
    // The API responds with a 404 error if the settings are not configured yet; don't retry in this case.
    if (error instanceof HTTPError) {
      return error.response.status !== 404;
    }

    return true;
  },
  staleTime: ms('60s'),
  ...options,
});

/**
 * React Query based custom hook for getting the AI model settings for a company with a given companyId.
 *
 * @param companyId - The ID of the company to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_ai_model_settings_by_company_id_vadmin_company__company_id__default_ai_model_settings_get
 */
export const useQueryCompanyAiSettings = (
  companyId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<CompanyAiSettings, Error, CompanyAiSettings>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<CompanyAiSettings, Error, CompanyAiSettings>(
    getCompanyAiSettingsQueryOptions<CompanyAiSettings>({
      companyId,
      options,
    }),
  );
